import { ActivitySummary, SummaryStatus } from 'types/ActivitySummary';

export const isActivitySummaryComplete = (activitySummary: ActivitySummary) => {
  return activitySummary.summary_status === SummaryStatus.COMPLETED;
};

export const isSummaryWithMultipleActivities = (activitySummary: ActivitySummary) => {
  return activitySummary.count > 1;
};

/**
 * Deduplicates Land Title Change events by permitnum.
 * Handles both flat event structures and nested structures with latest_activity.
 *
 * @param events - Array of events that can be either:
 *   - Flat structure with direct document_type and permitnum properties
 *   - Nested structure with latest_activity containing document_type and permitnum
 *   - Needed for the Land Title Change event to be deduplicated since those permit numbers are not unique
 *
 * @returns Filtered array with duplicate Land Title Change events removed
 * @preserves All non-Land Title Change events and events without permitnum
 */
export const dedupeLandTitleChangeEvents = <
  T extends
    | { document_type: string; permitnum?: string }
    | { latest_activity: { document_type: string; permitnum?: string } },
>(
  events: T[],
): T[] => {
  const seen = new Map();

  return events.filter((event) => {
    // Handle both flat and nested structures
    const docType =
      'latest_activity' in event
        ? event.latest_activity.document_type
        : event.document_type;

    const permitNum =
      'latest_activity' in event ? event.latest_activity.permitnum : event.permitnum;

    if (docType !== 'Land Title Change') {
      return true;
    }

    if (!permitNum) {
      return true;
    }

    if (!seen.has(permitNum)) {
      seen.set(permitNum, true);

      return true;
    }

    return false;
  });
};
